import React from "react";
import { CreativeConfigData, CreativeFeature, CreativeTopic, CreativeFeatureOption } from "../Shared/Party/PartyConfigData";
import { Checkbox, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { CreativeUserSubmission } from "../Shared/Creative/UserSubmission";
import { StateManagerContext } from "./StateManagerContext";
import { UIConfigContext } from "./UIConfigContext";
import { LandingPage } from "./LandingPage";
import { ActionButton, ActionHandler, CreateAction, EmptyActionData } from "./ActionButton";
import { FanboxStyling } from "./CSSUtilities";

export type CreativePanelProps = {
    config: CreativeConfigData;
    height: number;
}

type CreativeQuestionControlProps = {
    feature: CreativeFeature;
    submission: React.MutableRefObject<number[][]>;
    index: number;
    fontBase: number;
    width: number

}

const CreativeQuestionControl = ({ feature, submission, index, fontBase, width }: CreativeQuestionControlProps): JSX.Element => {

    const [singleValue, setSingleValue] = React.useState<string>('');
    const [multiValue, setMultiValue] = React.useState<string[]>([]);
    const changeValueSingle = (val: string) => {
        setSingleValue(val);
        feature.options.forEach((option, selIndex) => {
            if (val === option.text) {
                submission.current[index].splice(0);
                submission.current[index].push(selIndex);
            }
        })
    }

    const changeValueMultiple = (val: string[]) => {
        if (val.length > feature.selectCount)
            return;
        setMultiValue(val);
        submission.current[index].splice(0);
        val.forEach((v) => {
            feature.options.forEach((option, selIndex) => {
                if (v === option.text) {
                    submission.current[index].push(selIndex);
                }
            })
        })

    }

    if (feature.selectCount === 1) {
        return <div key={'CreativeQuestionControl' + index} style={{
            display: 'flex',
            flexDirection: 'column',
            width: width + 'px',
            minWidth: width + 'px',
            maxWidth: width + 'px'
        }}>
            <InputLabel id='message-boost-select-label'>{feature.text}</InputLabel>
            <Select
                labelId="message-boost-select-label"
                id="message-boost-select"
                label="Boost Level"
                value={singleValue}
                onChange={(ev: SelectChangeEvent) => { changeValueSingle(ev.target.value); }}
                sx={{
                    color: 'white',
                    backgroundColor: '#303030',
                    fontSize: fontBase,
                    borderRadius: '8px',
                    marginTop: '10px',
                    marginLeft: '30px',
                    marginRight: '30px',
                    marginBottom: '10px',
                }}
            >
                {feature.options.map((option, itemIndex) => {
                    return <MenuItem value={option.text} key={'CreativeQuestionControl' + index + '_item_' + itemIndex}>{option.text}</MenuItem>
                })}

            </Select>
        </div>

    }
    else {
        return <div key={'CreativeQuestionControl' + index} style={{
            display: 'flex',
            flexDirection: 'column',
            width: width + 'px',
            minWidth: width + 'px',
            maxWidth: width + 'px'
        }}>
            <InputLabel id='message-boost-select-label'>{feature.text}</InputLabel>
            <Select
                labelId="message-boost-select-label"
                id="message-boost-select"
                label="Boost Level"
                value={multiValue}
                onChange={(ev: SelectChangeEvent<string[]>) => {
                    const value = ev.target.value;
                    const v = typeof value === 'string' ? value.split(',') : value;
                    changeValueMultiple(v);
                }}
                renderValue={(selected) => selected.join(', ')}
                multiple
                sx={{
                    color: 'white',
                    backgroundColor: '#303030',
                    fontSize: fontBase,
                    borderRadius: '8px',
                    marginTop: '10px',
                    marginLeft: '30px',
                    marginRight: '30px',
                    marginBottom: '10px',
                }}
            >
                {feature.options.map((option, itemIndex) => {

                    return <MenuItem value={option.text} key={'CreativeQuestionControl' + index + '_item_' + itemIndex}>
                        <Checkbox checked={submission.current[index].includes(itemIndex)} />
                        <ListItemText primary={option.text} />
                    </MenuItem>
                })}

            </Select>
        </div>

    }


}

type CreativePanelState = 'landing' | 'topic' | 'feature' | 'artstyle' | 'thankyou';


export const CreativePanel = ({ config, height }: CreativePanelProps): JSX.Element => {
    const uiConfig = React.useContext(UIConfigContext);
    const submission = React.useRef<number[][]>((() => {
        const ret: number[][] = [];
        var maxFeatures = 0;
        for (let a = 0; a < config.topics.length; a++) {
            maxFeatures = Math.max(config.topics[a].features.length, maxFeatures);
        }

        for (let a = 0; a < maxFeatures; a++) {
            ret.push([]);
        }
        return ret;
    })());

    const [panelState, setPanelState] = React.useState<CreativePanelState>('landing');
    const [selectedTopic, setSelectedTopic] = React.useState<number>(0);
    const [selectedArtStyle, setSelectedArtStyle] = React.useState<number>(0);
    const stateManager = React.useContext(StateManagerContext);

    const makeCreative: ActionHandler = (_action) => {
        const user = stateManager.user();
        const submissions : CreativeUserSubmission = {
            topicSelection : selectedTopic,
            artStyleId : uiConfig.artStyles[selectedArtStyle].id,
            featureSelections : submission.current
        }
        const data = {
            itemId: config.itemId,
            submissions: submissions,
            userId: user.fanbox.id,
            accessToken: user.fanbox.accessToken ?? '',
            userNickname: user.userInformation.nickname,
            partyId: stateManager.partyId() ?? ''
        }

        const worker = async () => {
            const uri = '/api/creative';
            const response = await fetch(uri, {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(data)
            });
        }

        worker();
        setPanelState('thankyou');
        return true;

    }

    const makeAction = CreateAction('createAction', [
        {
            permission: 'signedIn'
        },
        {
            permission: 'boosts',
            threshold: 1000
        }
    ], makeCreative);
    const createStyling: FanboxStyling = {
        width: 186,
        height: 53,
        color: {
            background: 'blue',
            foreground: 'white',
        },
        font: {
            size: 18,
            weight: 'bold'
        },
        boundary: {
            radius: 75,
            margin: {
                quadType: 'margin',
                top: 20,
                left: 20,
                bottom: 20,
                right: 20
            }
        }
    }


    /* const paymentHandler = (isPaid : boolean) => {
         if(isPaid)
             makeCreative();
         else
             setPanelState('input');
     }*/



    const noEntryText = 'You need 1000 boosts to enter this competition';

    const landingEntryHandler: ActionHandler = (_action) => {
        setPanelState('topic');
        return true;
    }

    const landingAction = CreateAction('landingAction', [{ permission: 'signedIn' },
        {
            permission: 'boosts',
            threshold: 1000
        }], landingEntryHandler);

    const topicHandler: ActionHandler = (_action) => {
        setPanelState('feature');
        return true;
    }

    const topicAction = CreateAction('topicAction', [{ permission: 'signedIn' }], topicHandler);

    const featureHandler: ActionHandler = (_action) => {
        setPanelState('artstyle');
        return true;
    }

    const featureAction = CreateAction('featureAction', [{ permission: 'signedIn' }], featureHandler);

    if (!stateManager.user().userInformation.signUpComplete) {
        uiConfig.showEventOverlay('signIn', {});
    }

    if (panelState === 'landing') {
        return <LandingPage showEnterButton={true} height={height - 30} image={config.image} heading={config.title}
            subHeading={config.subTitle} description={config.description} buttonText={config.buttonText} action={landingAction} actionData={EmptyActionData} noEntryText={noEntryText} absolute={false} />
    }
    else if (panelState === 'topic') {
        return <div style={{
            width: uiConfig.width + 'px',
            minWidth: uiConfig.width + 'px',
            maxWidth: uiConfig.width + 'px',
            height: (height - 30) + 'px',
            maxHeight: (height - 30) + 'px',
            minHeight: (height - 30) + 'px',
            overflow: 'auto',
            backgroundColor: uiConfig.contolBackground
        }}>
            <div style={{
                width: uiConfig.width + 'px',
                minWidth: uiConfig.width + 'px',
                maxWidth: uiConfig.width + 'px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <span style={{ fontFamily: 'roboto', fontSize: (uiConfig.fontBase * 2) + 'px', fontWeight: 700 }}>
                    {config.title + ' ' + config.subTitle}
                </span>
                <span style={{ fontFamily: 'roboto', fontSize: uiConfig.fontBase + 'px', fontWeight: 400 }}>
                    {config.description}
                </span>
                {config.topics.map((topic, index) => {
                    const clickHandler = () => { setSelectedTopic(index) };
                    return <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: index === selectedTopic ? 'white' : uiConfig.contolBackground, border: '3px solid white' }}
                        onClick={(ev) => { clickHandler(); }}>
                        <span style={{ color: index === selectedTopic ? 'black' : 'white', fontFamily: 'roboto', fontSize: '16px' }}
                            onClick={(ev) => { clickHandler(); }}>
                            {topic.text}
                        </span>
                        <span style={{ color: index === selectedTopic ? uiConfig.fanboxColor('darkPink') : uiConfig.fanboxColor('pink'), fontFamily: 'roboto', fontSize: '20px', fontStyle: 'italic' }}
                            onClick={(ev) => { clickHandler(); }}>
                            {topic.attribution}
                        </span>
                    </div>;
                })}
                <ActionButton text="Select" action={topicAction} actionData={EmptyActionData} styling={createStyling} prompt={'center'} />

            </div>
        </div>
    }
    else if (panelState === 'feature') {

        return <div style={{
            width: uiConfig.width + 'px',
            minWidth: uiConfig.width + 'px',
            maxWidth: uiConfig.width + 'px',
            height: (height - 30) + 'px',
            maxHeight: (height - 30) + 'px',
            minHeight: (height - 30) + 'px',
            overflow: 'auto',
            backgroundColor: uiConfig.contolBackground
        }}>
            <div style={{
                width: uiConfig.width + 'px',
                minWidth: uiConfig.width + 'px',
                maxWidth: uiConfig.width + 'px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <span style={{ fontFamily: 'roboto', fontSize: (uiConfig.fontBase * 2) + 'px', fontWeight: 700 }}>
                    {config.title + ' ' + config.subTitle}
                </span>
                <span style={{ fontFamily: 'roboto', fontSize: uiConfig.fontBase + 'px', fontWeight: 400 }}>
                    {config.description}
                </span>
                {config.topics[selectedTopic].features.map((feature, index) => {
                    const cProps: CreativeQuestionControlProps = {
                        feature: feature,
                        submission: submission,
                        index: index,
                        fontBase: uiConfig.fontBase,
                        width: uiConfig.width
                    };
                    return <CreativeQuestionControl {...cProps} />;
                })}
                <ActionButton text="Next" action={featureAction} actionData={EmptyActionData} styling={createStyling} prompt={'center'} />

            </div>
        </div>
    }
    else if (panelState === 'artstyle') {
        const imgDim = '' + (0.5 * uiConfig.width -20) + 'px';
        return <div style={{
            width: uiConfig.width + 'px',
            minWidth: uiConfig.width + 'px',
            maxWidth: uiConfig.width + 'px',
            height: (height - 30) + 'px',
            maxHeight: (height - 30) + 'px',
            minHeight: (height - 30) + 'px',
            overflow: 'auto',
            backgroundColor: uiConfig.contolBackground
        }}>
            <div style={{
                width: uiConfig.width + 'px',
                minWidth: uiConfig.width + 'px',
                maxWidth: uiConfig.width + 'px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <span style={{ fontFamily: 'roboto', fontSize: (uiConfig.fontBase * 2) + 'px', fontWeight: 700 }}>
                    {config.title + ' ' + config.subTitle}
                </span>
                <span style={{ fontFamily: 'roboto', fontSize: uiConfig.fontBase + 'px', fontWeight: 400 }}>
                    {config.description}
                </span>
                {uiConfig.artStyles.map((style, index) => {
                    const clickHandler = () => { 
                        setSelectedArtStyle(index); 
                    };
                    return <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: index === selectedArtStyle ? 'white' : uiConfig.contolBackground, border: '3px solid white' }}
                        onClick={(ev) => { clickHandler(); }}>
                        <span style={{ color: index === selectedArtStyle ? 'black' : 'white', fontFamily: 'roboto', fontSize: '22px' }}
                            onClick={(ev) => { clickHandler(); }}>
                            {style.name}
                        </span>
                        <div style = {{display : 'flex', flexDirection : 'row'}}>
                            <div style={{ backgroundImage: 'url(' + style.image + ')', height: imgDim, width: imgDim, maxWidth : imgDim, maxHeight : imgDim, minHeight : imgDim, minWidth : imgDim, backgroundRepeat: "no-repeat", backgroundSize: "contain", backgroundPosition: 'center' , margin : '10px' }} ></div>
                        <span style={{ color: index === selectedArtStyle ? 'black' : 'white', fontFamily: 'roboto', fontSize: '16px' }}
                            onClick={(ev) => { clickHandler(); }}>
                            {style.description}
                        </span>
                        </div>
                    </div>;
                })}
                <ActionButton text={config.buttonText} action={makeAction} actionData={EmptyActionData} styling={createStyling} prompt={'center'} />

            </div>
        </div>
    }
    /*else if(panelState === 'payment') {
        return <PurchaseBoosts  height={height} boostNeed={1000} callback={paymentHandler}/>
     }*/
    else {
        return <div style={{
            width: uiConfig.width + 'px',
            minWidth: uiConfig.width + 'px',
            maxWidth: uiConfig.width + 'px',
            height: (height - 30) + 'px',
            maxHeight: (height - 30) + 'px',
            minHeight: (height - 30) + 'px',
            overflow: 'auto',
            backgroundColor: uiConfig.contolBackground
        }}>
            <div style={{
                width: uiConfig.width + 'px',
                minWidth: uiConfig.width + 'px',
                maxWidth: uiConfig.width + 'px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <span style={{ fontFamily: 'roboto', fontSize: (uiConfig.fontBase * 2) + 'px', fontWeight: 700 }}>
                    {config.title}
                </span>
                <span style={{ fontFamily: 'roboto', fontSize: uiConfig.fontBase + 'px', fontWeight: 400 }}>
                    {config.thankYouText}
                </span>
            </div>
        </div>
    }
}